/**
 * Mail-flow volume — an availability signal, not a security one.
 *
 * This is the panel that earns the board its place. Every other number here
 * gets *worse* when something is wrong; this one goes to zero. If Proofpoint
 * stops reporting, or mail flow itself stops, threat counts collapse and every
 * other panel quietly reads as a very good day. Both causes are P1 and neither
 * is otherwise visible anywhere in NOVA.
 *
 * So the alert is on the **floor**, not on a spike. The floor is a quarter of
 * the median of recent complete days, computed by the backend so the number in
 * the alert and the number on the bar cannot drift apart, and it is only
 * asserted once there is enough history for the median to mean anything. Today
 * is excluded from the judgement — it is still accumulating, and judging it
 * would raise a P1 every morning.
 */

import { Activity, Icon, TriangleAlert } from "@norican/icons";
import { Alert, Meter, MiniSparkline, Typography } from "@norican/react";

import type { Verdict } from "@/components/board";
import type { MailFlowPoint, SecuritySummary } from "@/lib/api";

import { PanelCard } from "./panel-card";
import { formatCount, formatDayLabel, mailFlowTone } from "./status";

interface MailFlowProps {
  /** Per-day totals, oldest first. */
  points: MailFlowPoint[];
  /** Headline figures carrying the floor and the breach flag. */
  summary: SecuritySummary;
  /** Whether the poller itself is current — a dead poller also reads as zero. */
  figuresCurrent: boolean;
}

export function MailFlow({ points, summary, figuresCurrent }: MailFlowProps) {
  const floor = summary.mail_flow_floor;
  const complete = points.filter((point) => point.is_complete);
  const peak = Math.max(1, ...points.map((point) => point.message_event_total), floor ?? 0);

  return (
    <PanelCard
      title="Mail-flow volume"
      caption={
        floor === null
          ? "Expected floor not yet established"
          : `Expected floor ${formatCount(floor)} observations/day`
      }
      verdict={mailFlowVerdict(summary, figuresCurrent)}
      note={
        <Typography variant="caption" tone="muted">
          The floor is a quarter of the median of recent complete days, so one quiet weekend or one
          campaign spike barely moves it. It is deliberately a <em>lower</em> bound: a collapse
          toward zero means Proofpoint stopped reporting or mail flow stopped, and both are P1. A
          spike is not alerted on here — that is what the threat panels above are for. Today is
          excluded from the judgement because it is still accumulating.
        </Typography>
      }
    >
      <div className="flex flex-col gap-5">
        {summary.mail_flow_below_floor && (
          <Alert
            tone="danger"
            icon={<Icon icon={TriangleAlert} size="sm" />}
            title="Mail-flow volume has collapsed below the expected floor"
          >
            {formatDayLabel(summary.mail_flow_last_complete_day ?? "")} recorded only{" "}
            {formatCount(summary.mail_flow_last_complete_total)} threat observations against an
            expected floor of {formatCount(floor)}. Either Proofpoint has stopped reporting or mail
            flow has stopped — both are P1. Treat the rest of this board as unreliable until this
            clears.
          </Alert>
        )}

        {!figuresCurrent && !summary.mail_flow_below_floor && (
          <Alert
            tone="warning"
            icon={<Icon icon={Activity} size="sm" />}
            title="These volumes may be incomplete"
          >
            The poller is not currently collecting, so a low reading here could be NOVA rather than
            Proofpoint. Check integration health below before escalating.
          </Alert>
        )}

        {points.length === 0 ? (
          <Typography variant="body-sm" tone="muted">
            No days recorded yet.
          </Typography>
        ) : (
          <>
            <div className="flex items-center justify-between gap-6">
              <div className="flex flex-col">
                <Typography variant="caption" tone="muted">
                  Today so far
                </Typography>
                <Typography variant="heading-4" as="p" tabular>
                  {formatCount(summary.mail_flow_today)}
                </Typography>
              </div>
              {points.length > 1 && (
                <MiniSparkline
                  data={points.map((point) => point.message_event_total)}
                  tone={summary.mail_flow_below_floor ? "danger" : "accent"}
                  endMarker="dot"
                  currentIndex={points.length - 1}
                  width={220}
                  height={40}
                  aria-label={`Threat observations per day over the last ${points.length} days`}
                />
              )}
            </div>

            <div className="flex flex-col gap-3">
              {points.map((point) => (
                <DayBar key={point.day} point={point} floor={floor} max={peak} />
              ))}
            </div>

            {complete.length === 0 && (
              <Typography variant="caption" tone="muted">
                No complete day in this window yet, so nothing has been judged against a floor.
              </Typography>
            )}
          </>
        )}
      </div>
    </PanelCard>
  );
}

interface DayBarProps {
  /** One day's total. */
  point: MailFlowPoint;
  /** The expected floor, or null when not yet established. */
  floor: number | null;
  /** Shared scale across every day, so the bars are comparable. */
  max: number;
}

function DayBar({ point, floor, max }: DayBarProps) {
  const tone = mailFlowTone(point.message_event_total, floor, point.is_complete);

  return (
    <Meter.Root
      value={Math.min(point.message_event_total, max)}
      min={0}
      max={max}
      size="sm"
      tone={tone === "neutral" ? "info" : tone}
      aria-valuetext={`${formatDayLabel(point.day)}: ${formatCount(
        point.message_event_total,
      )} threat observations${floor === null ? "" : `, floor ${formatCount(floor)}`}`}
    >
      <div className="mb-1 flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
        <Meter.Label>
          <Typography variant="caption" tone="muted">
            {formatDayLabel(point.day)}
            {point.is_complete ? "" : " (today, still accumulating)"}
          </Typography>
        </Meter.Label>
        <Meter.Value>
          {() => (
            <Typography variant="caption" tone={tone === "danger" ? "danger" : "muted"} tabular>
              {formatCount(point.message_event_total)}
              {tone === "danger" ? ` — below floor of ${formatCount(floor)}` : ""}
            </Typography>
          )}
        </Meter.Value>
      </div>
      <Meter.Track>
        <Meter.Indicator />
      </Meter.Track>
    </Meter.Root>
  );
}

/** What the volume means for the rest of the board. */
function mailFlowVerdict(summary: SecuritySummary, figuresCurrent: boolean): Verdict {
  if (summary.mail_flow_below_floor) {
    return {
      label: "Act",
      tone: "danger",
      text: "Volume collapsed below the expected floor: either Proofpoint stopped reporting or mail flow stopped. Both are P1, and everything else on this board is unreliable until it clears.",
    };
  }
  if (!figuresCurrent) {
    return {
      label: "Watch",
      tone: "warning",
      text: "The poller is not collecting normally, so a low reading here could be NOVA rather than Proofpoint.",
    };
  }
  if (summary.mail_flow_floor === null) {
    return {
      label: "Building",
      tone: "neutral",
      text: "Not enough complete days yet for a floor to mean anything, so nothing here is being judged.",
    };
  }
  return {
    label: "Holding",
    tone: "success",
    text: "Mail is flowing and Proofpoint is reporting. The threat counts above are measurements rather than silence.",
  };
}
